import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { CATERING, IMG, BUSINESS } from "../data/content";
import { scrollToHash } from "../lib/scroll";
import { SectionLabel } from "./Section";
import { fadeUp, stagger, viewport } from "../lib/motion";

const Catering = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="catering" ref={ref} className="relative py-16 sm:py-32 overflow-hidden" data-testid="catering-section">
      {/* Parallax background */}
      <motion.div style={{ y, scale: 1.15 }} className="absolute inset-0">
        <img src={IMG.charcuterie} alt="Detour catering spread" className="h-full w-full object-cover" loading="lazy" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/40" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-xl">
          <SectionLabel className="text-highlight">Catering</SectionLabel>
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-background"
          >
            Bring Detour to <span className="text-highlight">your table.</span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="mt-5 text-background/85 text-base sm:text-lg leading-relaxed"
          >
            From office mornings to full-scale events, we craft coffee service and Mediterranean platters that make every gathering memorable.
          </motion.p>
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {CATERING.map((c) => (
            <motion.div
              key={c.title}
              variants={fadeUp}
              className="rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md p-6 transition-colors duration-300 hover:bg-white/15"
              data-testid={`catering-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <h3 className="font-serif text-xl text-background">{c.title}</h3>
              <p className="mt-2 text-sm text-background/75 leading-relaxed">{c.copy}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.a
          href="#contact"
          onClick={(e) => { e.preventDefault(); scrollToHash("#contact"); }}
          data-testid="request-catering-btn"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="group mt-10 inline-flex items-center gap-2 rounded-full bg-highlight text-foreground px-8 py-4 text-sm font-semibold tracking-wide transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
        >
          Request Catering
          <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
        </motion.a>
      </div>
    </section>
  );
};

export default Catering;
